import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const PackageFacility = () => {
  const { user } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublicModal, setShowPublicModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [publicId, setPublicId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ id: '', name: '', image: null });
  const [imagePreview, setImagePreview] = useState('');
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('facilityList');
      setFacilities(response.data || []);
    } catch (error) {
      toast.error('Failed to load facilities');
      console.error('Facility fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({ id: '', name: '', image: null });
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (facility) => {
    setFormData({ id: facility._id, name: facility.name, image: null });
    setImagePreview(facility.image ? `${MEDIA_URL}${facility.image}` : '');
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error('Please choose a smaller image less than 1MB');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const data = new FormData();
      if (formData.id) data.append('id', formData.id);
      data.append('name', formData.name);
      if (formData.image) data.append('image', formData.image);

      const response = await apiService.postFormData('updateFacility', data);
      if (response.status) {
        toast.success('Facility updated successfully');
        setShowModal(false);
        fetchFacilities();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to save facility');
      console.error('Save error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.get(`deleteFacility/${deleteId}`);
      if (response.status) {
        setFacilities(facilities.filter(f => f._id !== deleteId));
        setShowDeleteModal(false);
        toast.success('Facility deleted successfully');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to delete facility');
      console.error('Delete error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakePublic = (id) => {
    setPublicId(id);
    setShowPublicModal(true);
  };

  const confirmMakePublic = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.post('makePublic', { id: publicId, type: 'facility' });
      if (response.status) {
        setFacilities(facilities.map(f => f._id === publicId ? { ...f, vendorId: null } : f));
        setShowPublicModal(false);
        toast.success('Published successfully');
      } else {
        toast.error('Something went wrong');
      }
    } catch (error) {
      toast.error('Failed to make public');
      console.error('Make public error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getDefaultImage = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNSAxNUwyOSAyM0gzM0wyNyAyOEwyOSAzNkwyNSAzMkwyMSAzNkwyMyAyOEwxNyAyM0gyMUwyNSAxNVoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cg==';
  };

  const canEdit = (facility) => {
    return user?.role === 'admin' || facility.vendorId === user?._id;
  };

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">Package Facility</h3>
          {user?.role === 'admin' && (
            <button
              onClick={handleAddNew}
              className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 flex items-center gap-2"
            >
              <i className="bi bi-plus-lg"></i>
              Add New
            </button>
          )}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : facilities.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-list-check text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Facilities Found</h3>
              <p className="text-gray-500">Add your first facility to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-4 py-3">Icon</th>
                    <th className="px-4 py-3">Name</th>
                    {user?.role === 'admin' ? <th className="px-4 py-3">Public</th> : <th className="px-4 py-3"></th>}
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {facilities.map((facility) => (
                    <tr key={facility._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <img
                          src={facility.image ? `${MEDIA_URL}${facility.image}` : getDefaultImage()}
                          alt={facility.name}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.target.src = getDefaultImage(); }}
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{facility.name}</td>
                      <td className="px-4 py-3">
                        {facility.vendorId && user?.role === 'admin' && (
                          <button
                            onClick={() => handleMakePublic(facility._id)}
                            className="text-green-600 hover:text-green-800"
                            title="Make Public"
                          >
                            <i className="bi bi-check-circle text-2xl"></i>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {canEdit(facility) && (
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(facility)}
                              className="text-green-600 hover:text-green-800"
                            >
                              <i className="bi bi-pencil text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(facility._id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="bi bi-trash text-lg"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Package Facility</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter Facility Name"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              {user?.role === 'admin' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-4">
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded border" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Max size: 1MB</p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this Facility?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Make Public Modal */}
      {showPublicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowPublicModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to make it public?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPublicModal(false)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmMakePublic}
                className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageFacility;
